import { computed, DestroyRef, inject, Injectable, resource, ResourceStatus } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable, retry, timer } from 'rxjs';
import { ApiConfigService } from '../api/api-config.service';

/**
 * Tentativas e backoff do `GET /api/platforms` (gowo.hub.front#839, incidente de produção):
 * na promoção da #836, o front entrou no ar antes da api que publica o endpoint — toda
 * requisição tomou 404 na janela entre os dois deploys, e sem retry o `resource` ficava preso
 * no `PLATFORM_REGISTRY` (14 plataformas) pelo resto da sessão, mesmo depois da api subir.
 * Backoff exponencial: 1s, 2s, 4s.
 */
const PLATFORMS_RETRY_COUNT = 3;
const PLATFORMS_RETRY_BASE_DELAY_MS = 1000;

export interface PlatformInfo {
  id: string;
  name: string;
  iconUrl: string;
}

/**
 * Shape mínima que usamos de GET /api/platforms (catálogo de plataformas para qualquer
 * autenticado; contrato kram.hub.api#1874, gowo.hub.front#836). `platformId` é o id do
 * manifesto do plugin — o mesmo `IdPlugin`/`IManifesto.id` usado no resto do app. Sem
 * `monthlyPrice` nem `status`: esses campos só existem em `AdminPlatformDTO`
 * (`/api/admin/platforms`, uso exclusivo da área `/admin`). O DTO não tem `iconUrl`: ícone é
 * sempre resolvido pelo `PLATFORM_REGISTRY` local (gowo.hub.front#742).
 */
interface PlatformCatalogDTO {
  platformId: string;
  name: string | null;
}

/** Envelope `Paginacao<PlatformCatalogDTO>` — mesmo padrão de /api/products e /api/customers. */
interface PlatformCatalogDTOPaginacao {
  items: PlatformCatalogDTO[] | null;
}

/**
 * `GET /api/platforms` com retry — isolado do `resource()` abaixo para ser testável sem
 * acionar a reatividade de signals do Angular (sem precedente de teste no projeto para
 * `resource()`; ver `platform.service.spec.ts`, describe do retry).
 */
export function fetchPlatformsPage(
  http: HttpClient,
  api: ApiConfigService,
): Observable<PlatformCatalogDTOPaginacao> {
  return http.get<PlatformCatalogDTOPaginacao>(api.url('/api/platforms'), {
    params: { skip: 0, take: 500 },
  }).pipe(
    retry({
      count: PLATFORMS_RETRY_COUNT,
      delay: (_error, retryAttempt) => timer(PLATFORMS_RETRY_BASE_DELAY_MS * 2 ** (retryAttempt - 1)),
    }),
  );
}

/**
 * Array estático mantido como fallback enquanto a requisição carrega
 * e como fonte de verdade para plataformas conhecidas localmente.
 * Utilizado como defaultValue do resource para garantir que os métodos
 * públicos nunca retornem listas/lookups vazios durante o carregamento.
 */
const PLATFORM_REGISTRY: PlatformInfo[] = [
  { id: 'b9b148c8-9375-4ae4-8445-647cb9600675', name: 'Mercado Livre',     iconUrl: '/flags/mercadolivre.svg' },
  { id: '2f2999d9-e423-4c65-9073-e2c65702eaa3', name: 'Protheus',          iconUrl: '/flags/protheus.svg'     },
  { id: '6ea8eb11-4db6-42fe-bae9-98865a89e209', name: 'Tiny',              iconUrl: '/flags/tiny.svg'         },
  { id: '430175fa-3a4a-4562-bd37-f3394da70bfd', name: 'Tray',              iconUrl: '/flags/tray.svg'         },
  { id: 'e38991e2-aefc-4c57-b4f7-46047867d52f', name: 'Comlog',            iconUrl: '/flags/comlog.svg'       },
  { id: '6abb6759-2b97-45a8-9dec-db7d4d3c15eb', name: 'Omie',              iconUrl: '/flags/omie.svg'         },
  { id: '0ea9b5b5-c33d-4076-9b41-4575132e68e9', name: 'Mtrix',             iconUrl: '/flags/mtrix.svg'        },
  { id: 'bcdfcebd-6a31-4fc2-b6f1-8478e2ba0cef', name: 'Bling',             iconUrl: '/flags/bling.svg'        },
  { id: 'f87d221b-f806-44aa-ac10-999725c9873b', name: 'Mercos',            iconUrl: '/flags/mercos.svg'       },
  { id: 'c989401b-9e97-4ce2-a3dd-54bbdd359d01', name: 'Mercos (Juratoys)', iconUrl: '/flags/mercos2.svg'      },
  { id: '4ca69f43-017f-4a72-a863-a24be65017d2', name: 'EscalaSoft',        iconUrl: '/flags/escalasoft.png'   },
  { id: '17cba1f0-47d2-4899-a58d-fa28561a455d', name: 'Entregou',          iconUrl: '/flags/entregou.png'     },
  { id: '6c57a697-cf9d-44e8-b40d-3a2ebf6be22e', name: 'Compleet [Módulo Cilindros]', iconUrl: '/flags/compleet-cilindros.png' },
  // id corrigido para o IdPlugin do manifesto (gowo.hub.front#742) — o valor antigo
  // (84cd863e-f36b-1410-851c-004ea920259c) tinha forma de Integration.Id, nunca batia.
  { id: '9ef77b4c-1b62-42d5-b929-b40abc609c66', name: 'Grafana',           iconUrl: '/flags/grafana.png'      },

  // Logos fixos no front (gowo.hub.front#839, decisão do lead 06/10/2026): kram.hub.api#2620
  // (rota de logo na api) foi cancelada — miniaturas de `Resources/Logo.png` de cada projeto do
  // KRAM.SDK (origin/main, medido em 06/10/2026), sem nenhuma dependência de contrato novo.
  // `platformId` = `BillingIntegrationPrices`/`IManifesto.Id`, medidos em produção em 05/10/2026.
  { id: '2619479a-6c4f-416f-89fb-f2eb1bfd4dbb', name: 'Amazon',                        iconUrl: '/flags/amazon.png'             },
  { id: '7db9e95e-f306-4fdb-a77c-de772b60c256', name: 'ChatGPT',                       iconUrl: '/flags/chatgpt.png'            },
  { id: 'aaad4cea-b1c4-44af-b1d4-d5729fd6204b', name: 'Compleet [ERP]',                iconUrl: '/flags/compleet-erp.png'       },
  { id: '1e75f4d2-010a-4ca5-b683-f4df7f1d8bfe', name: 'MultiChat',                     iconUrl: '/flags/multichat.png'          },
  { id: '43dd3a9e-7c9b-4358-a1e2-c99a674af308', name: 'Nuvemshop',                     iconUrl: '/flags/nuvemshop.png'          },
  { id: 'b001e666-5416-4db6-b390-af3b3d961ecd', name: 'RD Station',                    iconUrl: '/flags/rdstation.png'          },
  { id: 'ce715090-5e27-4c97-b3de-0c5850a79e56', name: 'Shop9',                         iconUrl: '/flags/shop9.png'              },
  { id: 'cdbc5d51-6b7f-405f-b481-21e6a6f92831', name: 'Shopee',                        iconUrl: '/flags/shopee.png'             },
  { id: 'e605808e-3c92-42d8-b7b4-d175382dcf64', name: 'Shopify',                       iconUrl: '/flags/shopify.png'            },
  { id: '4e68f510-c5ad-41ec-b837-8b2c9a13cee4', name: 'StarERP',                       iconUrl: '/flags/starerp.png'            },
  { id: 'a90eb17a-dfc0-45e5-8126-9fdc7dcdbe3a', name: 'Tiny [API 3.0]',                iconUrl: '/flags/tiny3.png'              },
  // Magalu (gowo.hub.front#974): miniatura de `Resources/Logo.png` do GOWO.Marketplace.Magalu
  // (KRAM.SDK origin/hmg, esqueleto da #1717 — marcador azul provisório até a arte final).
  { id: '9e87649e-28b0-4325-aa08-e192353fa92c', name: 'Magalu',                        iconUrl: '/flags/magalu.png'             },
];

const FALLBACK_ICON = '/flags/white.svg';
const FALLBACK_NAME = 'Plataforma desconhecida';

/** Lookup rápido do registry local para priorizar ícones SVG locais */
const REGISTRY_MAP = new Map<string, PlatformInfo>(
  PLATFORM_REGISTRY.map(p => [p.id, p])
);

/**
 * Converte o envelope paginado de `/api/platforms` em `PlatformInfo[]`.
 * Exportada só para o teste de regressão do envelope (gowo.hub.front#742) — não usar fora
 * do loader do `resource` abaixo.
 */
export function mapPlatformsResponse(page: PlatformCatalogDTOPaginacao): PlatformInfo[] {
  return (page.items ?? []).map<PlatformInfo>(dto => ({
    id:      dto.platformId,
    name:    dto.name ?? REGISTRY_MAP.get(dto.platformId)?.name ?? FALLBACK_NAME,
    iconUrl: REGISTRY_MAP.get(dto.platformId)?.iconUrl ?? FALLBACK_ICON,
  }));
}

/**
 * PlatformService — fonte única de verdade para metadados de plataformas.
 *
 * Carrega a lista de plataformas dinamicamente via GET /api/platforms (qualquer autenticado;
 * até gowo.hub.front#836 este serviço lia /api/admin/platforms — trocado porque esse endpoint
 * passou a exigir `Acesso-Admin`, kram.hub.api#1874). A área `/admin` continua usando
 * `/api/admin/platforms` via `AdminPlatformService`, que também traz preço e status.
 * Enquanto a requisição está em andamento (ou em caso de falha), o PLATFORM_REGISTRY
 * estático é usado como fallback — garantindo que os métodos públicos nunca retornem
 * listas vazias.
 *
 * Os métodos públicos são síncronos: leem do computed que é sempre alimentado.
 *
 * Uso básico:
 *   const name = this.platformService.getName(platformId);
 *   const icon = this.platformService.getIconUrl(platformId);
 *   const info = this.platformService.getInfo(platformId);
 *   const all  = this.platformService.getAll();
 */
@Injectable({ providedIn: 'root' })
export class PlatformService {

  private readonly http = inject(HttpClient);
  private readonly api  = inject(ApiConfigService);

  /**
   * Resource que carrega a lista de plataformas do backend. `/api/platforms` usa o mesmo
   * `InputQuery` paginado do admin (`Skip`/`Take`, envelope `Paginacao<PlatformCatalogDTO>`) —
   * pedimos `take` grande o bastante para trazer o catálogo inteiro numa chamada só (24 linhas
   * hoje; ver gowo.hub.front#742: com o `take` default do backend, a lista vinha cortada e o
   * sintoma reaparecia pra quem ficasse fora da primeira página).
   *
   * `retry` (gowo.hub.front#839): falha isolada (404 na janela de deploy, instabilidade de
   * rede) não pode prender o `resource` no `PLATFORM_REGISTRY` pelo resto da sessão — 3
   * tentativas com backoff antes de cair no fallback.
   *
   * Nome vem sempre do backend quando presente; o registry local só preenche nome/ícone
   * quando o backend não tem a plataforma ou não manda nome — ícone nunca vem do backend
   * (o DTO não tem esse campo).
   */
  private readonly _platforms = resource<PlatformInfo[], undefined>({
    loader: () => firstValueFrom(fetchPlatformsPage(this.http, this.api)).then(mapPlatformsResponse),
    defaultValue: PLATFORM_REGISTRY,
  });

  constructor() {
    /**
     * Recarrega ao voltar o foco depois de falha (gowo.hub.front#839) — mesmo padrão de
     * `separacao-detalhe.component.ts` (`onVisibilityChange`/revalidação silenciosa). Sem
     * isso, quem abriu a aba durante a falha só vê o catálogo completo depois de um F5: as 3
     * tentativas com backoff já se esgotaram no load inicial, e o `resource` não tenta de novo
     * por conta própria.
     */
    const reloadOnFocusAfterError = (): void => {
      if (document.visibilityState === 'visible' && this._platforms.status() === ResourceStatus.Error) {
        this._platforms.reload();
      }
    };
    document.addEventListener('visibilitychange', reloadOnFocusAfterError);
    inject(DestroyRef).onDestroy(() =>
      document.removeEventListener('visibilitychange', reloadOnFocusAfterError),
    );
  }

  /**
   * Map derivado para lookups O(1).
   * Sempre alimentado: usa defaultValue durante o carregamento.
   */
  private readonly _platformMap = computed(() =>
    new Map<string, PlatformInfo>(
      this._platforms.value().map(p => [p.id, p])
    )
  );

  /**
   * Retorna o nome legível da plataforma.
   * Retorna FALLBACK_NAME quando o ID não for reconhecido.
   */
  getName(platformId: string): string {
    return this._platformMap().get(platformId)?.name ?? FALLBACK_NAME;
  }

  /**
   * Retorna o caminho do ícone SVG da plataforma.
   * Retorna o ícone branco genérico quando o ID não for reconhecido.
   */
  getIconUrl(platformId: string): string {
    return this._platformMap().get(platformId)?.iconUrl ?? FALLBACK_ICON;
  }

  /**
   * Retorna o objeto completo da plataforma, ou null se não encontrada.
   */
  getInfo(platformId: string): PlatformInfo | null {
    return this._platformMap().get(platformId) ?? null;
  }

  /**
   * Retorna todas as plataformas em ordem alfabética de nome.
   * Útil para popular dropdowns/filtros de seleção de plataforma.
   */
  getAll(): PlatformInfo[] {
    return [...this._platforms.value()].sort((a, b) =>
      a.name.localeCompare(b.name, 'pt-BR')
    );
  }

  /**
   * Verifica se um ID de plataforma é conhecido.
   */
  isKnown(platformId: string): boolean {
    return this._platformMap().has(platformId);
  }
}
